import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Globe, MessageCircle, BookOpen, Calendar, Leaf, Users, Heart, Send, X, ChevronDown, Menu, ExternalLink, Mail, Share2, Facebook, Twitter, Linkedin, Link2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language configurations
const LANGUAGES = {
  en: { name: "English", flag: "🇬🇧" },
  es: { name: "Español", flag: "🇪🇸" },
  it: { name: "Italiano", flag: "🇮🇹" },
  fr: { name: "Français", flag: "🇫🇷" },
  de: { name: "Deutsch", flag: "🇩🇪" },
  ru: { name: "Русский", flag: "🇷🇺" }
};

// Translation strings - accessible, youth-friendly language
const TRANSLATIONS = {
  en: {
    tagline: "Timeless thoughts. New world.",
    subtitle: "To start with, dive into India's traditions — practical takes on finding balance, meaning, and your place in a chaotic world. Insights into Chinese, Japanese, Myanmar, Sri Lankan and Far East Asian cultures coming soon.",
    beliefLine: "No beliefs required. Just bring your curiosity.",
    startJourney: "Explore",
    askGuide: "Ask Anything",
    articles: "Ideas Worth Exploring",
    articlesDesc: "Bite-sized wisdom for busy minds",
    festivals: "What's Coming Up",
    festivalsDesc: "Celebrations that actually mean something",
    meditation: "Calm Your Mind",
    meditationDesc: "Simple practices that actually work",
    environment: "Live Lighter",
    environmentDesc: "What our ancestors figured out about sustainability",
    specialists: "Learn From Someone",
    specialistsDesc: "Real teachers, real connections",
    community: "Join the Conversation",
    communityDesc: "Ask questions, share thoughts, find your people",
    chatPlaceholder: "What's on your mind? Ask anything...",
    readMore: "Read More",
    bookSession: "Book Session",
    contact: "Say Hi",
    viewAll: "See All",
    discussions: "Chats",
    questions: "Q&A",
    footerTagline: "Stay curious. Keep exploring.",
    navHome: "Home",
    navArticles: "Ideas",
    navFestivals: "Calendar",
    navMeditation: "Practices",
    navCommunity: "Community"
  },
  es: {
    tagline: "Pensamientos eternos. Nuevo mundo.",
    subtitle: "Para empezar, sumérgete en las tradiciones de la India — enfoques prácticos para encontrar equilibrio, sentido y tu lugar en un mundo caótico. Próximamente: perspectivas de las culturas china, japonesa, birmana, cingalesa y del Lejano Oriente asiático.",
    beliefLine: "Sin creencias requeridas. Solo trae tu curiosidad.",
    startJourney: "Explorar",
    askGuide: "Pregunta Lo Que Sea",
    articles: "Ideas Que Vale la Pena Explorar",
    articlesDesc: "Sabiduría en pequeñas dosis para mentes ocupadas",
    festivals: "Lo Que Viene",
    festivalsDesc: "Celebraciones que realmente significan algo",
    meditation: "Calma Tu Mente",
    meditationDesc: "Prácticas simples que realmente funcionan",
    environment: "Vive Más Ligero",
    environmentDesc: "Lo que nuestros ancestros descubrieron sobre sostenibilidad",
    specialists: "Aprende de Alguien",
    specialistsDesc: "Maestros reales, conexiones reales",
    community: "Únete a la Conversación",
    communityDesc: "Haz preguntas, comparte ideas, encuentra tu gente",
    chatPlaceholder: "¿Qué tienes en mente? Pregunta lo que sea...",
    readMore: "Leer Más",
    bookSession: "Reservar",
    contact: "Saludar",
    viewAll: "Ver Todo",
    discussions: "Charlas",
    questions: "Preguntas",
    footerTagline: "Mantén la curiosidad. Sigue explorando.",
    navHome: "Inicio",
    navArticles: "Ideas",
    navFestivals: "Calendario",
    navMeditation: "Prácticas",
    navCommunity: "Comunidad"
  },
  it: {
    tagline: "Pensieri senza tempo. Nuovo mondo.",
    subtitle: "Per iniziare, immergiti nelle tradizioni dell'India — approcci pratici per trovare equilibrio, significato e il tuo posto in un mondo caotico. Prossimamente: approfondimenti sulle culture cinese, giapponese, birmana, cingalese e dell'Estremo Oriente asiatico.",
    beliefLine: "Nessuna credenza richiesta. Porta solo la tua curiosità.",
    startJourney: "Esplora",
    askGuide: "Chiedi Qualsiasi Cosa",
    articles: "Idee Che Vale la Pena Esplorare",
    articlesDesc: "Saggezza in pillole per menti impegnate",
    festivals: "Cosa c'è in Arrivo",
    festivalsDesc: "Celebrazioni che significano davvero qualcosa",
    meditation: "Calma la Tua Mente",
    meditationDesc: "Pratiche semplici che funzionano davvero",
    environment: "Vivi Più Leggero",
    environmentDesc: "Cosa hanno capito i nostri antenati sulla sostenibilità",
    specialists: "Impara da Qualcuno",
    specialistsDesc: "Insegnanti veri, connessioni vere",
    community: "Unisciti alla Conversazione",
    communityDesc: "Fai domande, condividi pensieri, trova la tua gente",
    chatPlaceholder: "Cosa hai in mente? Chiedi qualsiasi cosa...",
    readMore: "Leggi di Più",
    bookSession: "Prenota",
    contact: "Saluta",
    viewAll: "Vedi Tutto",
    discussions: "Chat",
    questions: "Domande",
    footerTagline: "Resta curioso. Continua a esplorare.",
    navHome: "Home",
    navArticles: "Idee",
    navFestivals: "Calendario",
    navMeditation: "Pratiche",
    navCommunity: "Comunità"
  },
  fr: {
    tagline: "Pensées intemporelles. Nouveau monde.",
    subtitle: "Pour commencer, plongez dans les traditions de l'Inde — des approches pratiques pour trouver l'équilibre, le sens et votre place dans un monde chaotique. Bientôt : aperçus des cultures chinoise, japonaise, birmane, sri-lankaise et d'Extrême-Orient asiatique.",
    beliefLine: "Aucune croyance requise. Apportez juste votre curiosité.",
    startJourney: "Explorer",
    askGuide: "Demandez N'importe Quoi",
    articles: "Idées à Explorer",
    articlesDesc: "Sagesse en bouchées pour esprits occupés",
    festivals: "Ce Qui Arrive",
    festivalsDesc: "Des célébrations qui ont vraiment du sens",
    meditation: "Apaisez Votre Esprit",
    meditationDesc: "Des pratiques simples qui marchent vraiment",
    environment: "Vivez Plus Léger",
    environmentDesc: "Ce que nos ancêtres savaient sur la durabilité",
    specialists: "Apprenez de Quelqu'un",
    specialistsDesc: "De vrais enseignants, de vraies connexions",
    community: "Rejoignez la Conversation",
    communityDesc: "Posez des questions, partagez, trouvez vos gens",
    chatPlaceholder: "Qu'avez-vous en tête ? Demandez n'importe quoi...",
    readMore: "Lire Plus",
    bookSession: "Réserver",
    contact: "Dire Bonjour",
    viewAll: "Voir Tout",
    discussions: "Discussions",
    questions: "Questions",
    footerTagline: "Restez curieux. Continuez à explorer.",
    navHome: "Accueil",
    navArticles: "Idées",
    navFestivals: "Calendrier",
    navMeditation: "Pratiques",
    navCommunity: "Communauté"
  },
  de: {
    tagline: "Zeitlose Gedanken. Neue Welt.",
    subtitle: "Zum Einstieg tauche ein in Indiens Traditionen — praktische Ansätze, um Balance, Sinn und deinen Platz in einer chaotischen Welt zu finden. Bald: Einblicke in chinesische, japanische, myanmarische, sri-lankische und fernöstlich-asiatische Kulturen.",
    beliefLine: "Kein Glaube nötig. Bring einfach deine Neugier mit.",
    startJourney: "Entdecken",
    askGuide: "Frag Einfach",
    articles: "Ideen, Die Es Wert Sind",
    articlesDesc: "Weisheit in kleinen Happen für beschäftigte Köpfe",
    festivals: "Was Kommt",
    festivalsDesc: "Feste, die wirklich etwas bedeuten",
    meditation: "Beruhige Deinen Geist",
    meditationDesc: "Einfache Praktiken, die wirklich funktionieren",
    environment: "Lebe Leichter",
    environmentDesc: "Was unsere Vorfahren über Nachhaltigkeit wussten",
    specialists: "Lerne Von Jemandem",
    specialistsDesc: "Echte Lehrer, echte Verbindungen",
    community: "Werde Teil des Gesprächs",
    communityDesc: "Stelle Fragen, teile Gedanken, finde deine Leute",
    chatPlaceholder: "Was beschäftigt dich? Frag einfach...",
    readMore: "Mehr Lesen",
    bookSession: "Buchen",
    contact: "Hallo Sagen",
    viewAll: "Alle Sehen",
    discussions: "Chats",
    questions: "Fragen",
    footerTagline: "Bleib neugierig. Entdecke weiter.",
    navHome: "Start",
    navArticles: "Ideen",
    navFestivals: "Kalender",
    navMeditation: "Praktiken",
    navCommunity: "Community"
  },
  ru: {
    tagline: "Вечные мысли. Новый мир.",
    subtitle: "Для начала погрузитесь в традиции Индии — практичные подходы к поиску баланса, смысла и своего места в хаотичном мире. Скоро: культуры Китая, Японии, Мьянмы, Шри-Ланки и Дальнего Востока Азии.",
    beliefLine: "Никаких убеждений. Просто принесите своё любопытство.",
    startJourney: "Исследовать",
    askGuide: "Спроси Что Угодно",
    articles: "Идеи для Размышления",
    articlesDesc: "Мудрость небольшими порциями для занятых умов",
    festivals: "Что Впереди",
    festivalsDesc: "Праздники, которые действительно что-то значат",
    meditation: "Успокой Свой Ум",
    meditationDesc: "Простые практики, которые реально работают",
    environment: "Живи Легче",
    environmentDesc: "Что наши предки знали об устойчивости",
    specialists: "Учись у Кого-то",
    specialistsDesc: "Настоящие учителя, настоящие связи",
    community: "Присоединяйся к Разговору",
    communityDesc: "Задавай вопросы, делись мыслями, найди своих людей",
    chatPlaceholder: "Что у тебя на уме? Спрашивай...",
    readMore: "Читать Дальше",
    bookSession: "Забронировать",
    contact: "Написать",
    viewAll: "Все",
    discussions: "Чаты",
    questions: "Вопросы",
    footerTagline: "Оставайся любопытным. Продолжай исследовать.",
    navHome: "Главная",
    navArticles: "Идеи",
    navFestivals: "Календарь",
    navMeditation: "Практики",
    navCommunity: "Сообщество"
  }
};

// Future Links Dropdown (Inactive for now)
const FutureLinksDropdown = ({ onSelectCulture }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const cultureLinks = [
    { label: "China", culture: "china", active: true },
    { label: "Japan", culture: "japan", active: true },
    { label: "Myanmar", culture: "myanmar", active: true },
    { label: "Sri Lanka", culture: "srilanka", active: true },
  ];
  
  const otherLinks = [
    { label: "Partner Sites", disabled: true },
    { label: "Resources", disabled: true },
    { label: "Courses", disabled: true },
    { label: "Events", disabled: true },
  ];
  
  const handleCultureClick = (culture) => {
    onSelectCulture(culture);
    setIsOpen(false);
  };
  
  return (
    <div className="lang-selector" data-testid="future-links-dropdown">
      <button 
        className="lang-btn more-btn" 
        onClick={() => setIsOpen(!isOpen)}
        data-testid="future-links-button"
      >
        <span>More</span>
        <ChevronDown size={14} />
      </button>
      {isOpen && (
        <div className="lang-dropdown more-dropdown" data-testid="future-links-menu">
          <div className="dropdown-section-label">Explore Cultures</div>
          {cultureLinks.map((link, idx) => (
            <div
              key={idx}
              className="lang-option future-link-item"
              onClick={() => handleCultureClick(link.culture)}
              style={{ 
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.8125rem'
              }}
            >
              {link.label}
            </div>
          ))}
          <div className="dropdown-divider"></div>
          <div className="dropdown-section-label" style={{ opacity: 0.6, fontSize: '0.75rem' }}>Coming Soon</div>
          {otherLinks.map((link, idx) => (
            <div
              key={idx}
              className="lang-option future-link-item"
              style={{ 
                opacity: 0.6, 
                cursor: 'not-allowed',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.8125rem'
              }}
            >
              {link.label}
              <span className="soon-tag">Soon</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Disclaimer Section
const DisclaimerSection = ({ language }) => {
  const disclaimers = {
    en: {
      title: "Disclaimer & Terms",
      content: "The content on this website is provided for informational and educational purposes only. Living Heritage does not claim to represent any official religious authority or institution. The philosophical and cultural insights shared here reflect interpretations and perspectives that may vary across traditions and scholars.",
      accuracy: "Accuracy: While we strive for accuracy, cultural and philosophical concepts can have multiple valid interpretations. We encourage readers to explore multiple sources and form their own understanding.",
      liability: "Liability: Living Heritage, its contributors, and partners accept no liability for any actions taken based on information provided on this site. External links are provided for convenience and do not constitute endorsement.",
      medical: "Health & Wellness: Meditation and wellness practices described are not substitutes for professional medical advice. Please consult healthcare professionals before beginning any new practice.",
      copyright: "Content: All original content is © Living Heritage. Traditional knowledge belongs to its respective cultural heritage.",
      contact: "Questions? We welcome dialogue and corrections."
    },
    es: {
      title: "Aviso Legal y Términos",
      content: "El contenido de este sitio web se proporciona únicamente con fines informativos y educativos. Living Heritage no pretende representar ninguna autoridad religiosa oficial o institución.",
      accuracy: "Precisión: Aunque nos esforzamos por la exactitud, los conceptos culturales y filosóficos pueden tener múltiples interpretaciones válidas.",
      liability: "Responsabilidad: Living Heritage, sus colaboradores y socios no aceptan ninguna responsabilidad por las acciones tomadas en base a la información proporcionada en este sitio.",
      medical: "Salud y Bienestar: Las prácticas de meditación y bienestar descritas no sustituyen el consejo médico profesional.",
      copyright: "Contenido: Todo el contenido original es © Living Heritage.",
      contact: "¿Preguntas? Agradecemos el diálogo y las correcciones."
    },
    fr: {
      title: "Avis de Non-Responsabilité",
      content: "Le contenu de ce site est fourni à des fins d'information et d'éducation uniquement. Living Heritage ne prétend représenter aucune autorité religieuse officielle.",
      accuracy: "Exactitude: Bien que nous nous efforcions d'être précis, les concepts culturels et philosophiques peuvent avoir de multiples interprétations valides.",
      liability: "Responsabilité: Living Heritage, ses contributeurs et partenaires n'acceptent aucune responsabilité pour les actions entreprises sur la base des informations fournies.",
      medical: "Santé et Bien-être: Les pratiques de méditation décrites ne remplacent pas les conseils médicaux professionnels.",
      copyright: "Contenu: Tout le contenu original est © Living Heritage.",
      contact: "Des questions? Nous accueillons le dialogue et les corrections."
    },
    de: {
      title: "Haftungsausschluss & Bedingungen",
      content: "Die Inhalte dieser Website dienen ausschließlich Informations- und Bildungszwecken. Living Heritage erhebt keinen Anspruch darauf, eine offizielle religiöse Autorität zu vertreten.",
      accuracy: "Genauigkeit: Obwohl wir uns um Genauigkeit bemühen, können kulturelle und philosophische Konzepte mehrere gültige Interpretationen haben.",
      liability: "Haftung: Living Heritage, seine Mitwirkenden und Partner übernehmen keine Haftung für Handlungen, die auf Grundlage der bereitgestellten Informationen erfolgen.",
      medical: "Gesundheit & Wellness: Die beschriebenen Meditations- und Wellness-Praktiken ersetzen keine professionelle medizinische Beratung.",
      copyright: "Inhalt: Alle Originalinhalte sind © Living Heritage.",
      contact: "Fragen? Wir begrüßen Dialog und Korrekturen."
    },
    it: {
      title: "Disclaimer e Termini",
      content: "I contenuti di questo sito sono forniti solo a scopo informativo ed educativo. Living Heritage non pretende di rappresentare alcuna autorità religiosa ufficiale.",
      accuracy: "Accuratezza: Sebbene ci sforziamo di essere accurati, i concetti culturali e filosofici possono avere molteplici interpretazioni valide.",
      liability: "Responsabilità: Living Heritage, i suoi collaboratori e partner non accettano alcuna responsabilità per azioni intraprese sulla base delle informazioni fornite.",
      medical: "Salute e Benessere: Le pratiche di meditazione descritte non sostituiscono i consigli medici professionali.",
      copyright: "Contenuto: Tutti i contenuti originali sono © Living Heritage.",
      contact: "Domande? Accogliamo il dialogo e le correzioni."
    },
    ru: {
      title: "Отказ от Ответственности",
      content: "Содержимое этого сайта предоставляется исключительно в информационных и образовательных целях. Living Heritage не претендует на представление какой-либо официальной религиозной власти.",
      accuracy: "Точность: Хотя мы стремимся к точности, культурные и философские концепции могут иметь множество допустимых интерпретаций.",
      liability: "Ответственность: Living Heritage, его участники и партнеры не несут ответственности за действия, предпринятые на основе предоставленной информации.",
      medical: "Здоровье: Описанные практики медитации не заменяют профессиональную медицинскую консультацию.",
      copyright: "Контент: Весь оригинальный контент © Living Heritage.",
      contact: "Вопросы? Мы приветствуем диалог и исправления."
    }
  };
  
  const d = disclaimers[language] || disclaimers.en;
  
  return (
    <section className="disclaimer-section" data-testid="disclaimer-section">
      <div className="container">
        <h3 className="disclaimer-title">{d.title}</h3>
        <div className="disclaimer-content">
          <p>{d.content}</p>
          <p><strong>{d.accuracy.split(':')[0]}:</strong> {d.accuracy.split(':').slice(1).join(':')}</p>
          <p><strong>{d.liability.split(':')[0]}:</strong> {d.liability.split(':').slice(1).join(':')}</p>
          <p><strong>{d.medical.split(':')[0]}:</strong> {d.medical.split(':').slice(1).join(':')}</p>
          <p>{d.copyright}</p>
          <p style={{ marginTop: '1rem', fontStyle: 'italic' }}>{d.contact}</p>
        </div>
      </div>
    </section>
  );
};

// Language Selector Component
const LanguageSelector = ({ language, setLanguage }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="lang-selector" data-testid="language-selector">
      <button 
        className="lang-btn lang-btn-compact" 
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-selector-button"
      >
        <Globe size={14} />
        <span className="lang-flag">{LANGUAGES[language].flag}</span>
        <span className="lang-name-full">{LANGUAGES[language].name}</span>
        <ChevronDown size={12} />
      </button>
      {isOpen && (
        <div className="lang-dropdown" data-testid="language-dropdown">
          {Object.entries(LANGUAGES).map(([code, { name, flag }]) => (
            <div
              key={code}
              className={`lang-option ${language === code ? 'active' : ''}`}
              onClick={() => { setLanguage(code); setIsOpen(false); }}
              data-testid={`language-option-${code}`}
            >
              {flag} {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// App name
const APP_NAME = "Living Heritage";

// Share URLs helper
const getShareUrls = (url, title) => ({
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`
});

// Share Button Component
const ShareButtons = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const shareUrls = getShareUrls(url || window.location.href, title || "Living Heritage - Timeless thoughts. New world.");
  
  const copyLink = () => {
    navigator.clipboard.writeText(url || window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginRight: '0.5rem' }}>Share:</span>
      <a href={shareUrls.facebook} target="_blank" rel="noopener noreferrer" className="share-btn" title="Share on Facebook" data-testid="share-facebook">
        <Facebook size={16} />
      </a>
      <a href={shareUrls.twitter} target="_blank" rel="noopener noreferrer" className="share-btn" title="Share on Twitter" data-testid="share-twitter">
        <Twitter size={16} />
      </a>
      <a href={shareUrls.linkedin} target="_blank" rel="noopener noreferrer" className="share-btn" title="Share on LinkedIn" data-testid="share-linkedin">
        <Linkedin size={16} />
      </a>
      <a href={shareUrls.whatsapp} target="_blank" rel="noopener noreferrer" className="share-btn" title="Share on WhatsApp" data-testid="share-whatsapp">
        <Send size={16} />
      </a>
      <button onClick={copyLink} className="share-btn" title="Copy link" data-testid="share-copy">
        {copied ? <Check size={16} /> : <Link2 size={16} />}
      </button>
    </div>
  );
};

// Email Signup Component
const EmailSignup = ({ language, t }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setStatus('loading');
    try {
      const response = await axios.post(`${API}/subscribe`, {
        email,
        name,
        language,
        interests: ['general']
      });
      setStatus('success');
      setMessage(response.data.message);
      setEmail('');
      setName('');
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };
  
  return (
    <section className="section" style={{ background: 'var(--brand-primary)', padding: '4rem 1.5rem' }} data-testid="email-signup-section">
      <div className="container" style={{ maxWidth: '600px', textAlign: 'center' }}>
        <Mail size={40} style={{ color: 'var(--text-primary)', marginBottom: '1rem' }} />
        <h2 style={{ fontSize: '1.75rem', fontWeight: 400, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
          {language === 'en' ? 'Stay Curious' : t.footerTagline.split('.')[0]}
        </h2>
        <p style={{ color: 'var(--text-primary)', opacity: 0.8, marginBottom: '1.5rem' }}>
          {language === 'en' ? 'Get thoughtful ideas delivered to your inbox. No spam, just wisdom.' : 'Subscribe for updates'}
        </p>
        
        {status === 'success' ? (
          <div style={{ background: 'rgba(255,255,255,0.3)', padding: '1.5rem', borderRadius: '1rem' }}>
            <Check size={32} style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }} />
            <p style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <input
              type="text"
              placeholder={language === 'en' ? 'Your name (optional)' : 'Name'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                padding: '0.875rem 1.25rem',
                borderRadius: '9999px',
                border: 'none',
                background: 'rgba(255,255,255,0.9)',
                fontSize: '1rem',
                color: 'var(--text-primary)'
              }}
              data-testid="signup-name"
            />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <input
                type="email"
                placeholder={language === 'en' ? 'Your email address' : 'Email'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  flex: 1,
                  padding: '0.875rem 1.25rem',
                  borderRadius: '9999px',
                  border: 'none',
                  background: 'rgba(255,255,255,0.9)',
                  fontSize: '1rem',
                  color: 'var(--text-primary)'
                }}
                data-testid="signup-email"
              />
              <button 
                type="submit" 
                disabled={status === 'loading'}
                style={{
                  padding: '0.875rem 1.5rem',
                  borderRadius: '9999px',
                  border: 'none',
                  background: 'var(--text-primary)',
                  color: 'white',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                data-testid="signup-submit"
              >
                {status === 'loading' ? <div className="spinner" style={{ width: '16px', height: '16px' }}></div> : <Send size={18} />}
                {language === 'en' ? 'Join' : 'OK'}
              </button>
            </div>
            {status === 'error' && (
              <p style={{ color: '#c53030', fontSize: '0.875rem' }}>{message}</p>
            )}
          </form>
        )}
        
        <p style={{ fontSize: '0.75rem', color: 'var(--text-primary)', opacity: 0.6, marginTop: '1rem' }}>
          {language === 'en' ? 'We respect your privacy. Unsubscribe anytime.' : ''}
        </p>
      </div>
    </section>
  );
};

// Navigation Component
const Navigation = ({ language, setLanguage, t, onAskGuide, onSelectCulture }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  
  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50" data-testid="main-navigation">
      <div className="nav-container">
        {/* Left side: Logo + Ask */}
        <div className="nav-left">
          <a href="/" className="nav-logo" data-testid="logo-link">
            <Heart size={20} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
            <div className="nav-logo-text">
              <span className="nav-logo-name">{APP_NAME}</span>
              <span className="nav-logo-tagline">Authentic and Easy</span>
            </div>
          </a>
          
          <button className="btn-ask-nav" onClick={onAskGuide} data-testid="nav-ask-guide-btn">
            <MessageCircle size={14} />
            <span className="ask-text">Ask</span>
          </button>
        </div>
        
        {/* Right side: More + Language */}
        <div className="nav-right">
          <FutureLinksDropdown onSelectCulture={onSelectCulture} />
          <LanguageSelector language={language} setLanguage={setLanguage} />
        </div>
      </div>
    </nav>
  );
};

// Chat Modal Component
const ChatModal = ({ isOpen, onClose, language, t }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage,
        language: language
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, I encountered an error. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="chat-modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="chat-modal">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/b81b5205-22f3-4d75-87bf-f3580bcccc6c/images/7767a2fcef058ad261b1f86d77bbaf17134c6a11f85ffe23857bf0b53f46abdf.png"
              alt="Guide"
              className="guru-orb"
              style={{ width: '48px', height: '48px' }}
            />
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 500 }}>Cultural Guide</h3>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Your companion for exploring Indian heritage</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} data-testid="chat-close-btn">
            <X size={24} />
          </button>
        </div>
        
        <div className="chat-messages" style={{ minHeight: '400px' }} data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center" style={{ padding: '3rem 1rem', color: 'var(--text-secondary)' }}>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b81b5205-22f3-4d75-87bf-f3580bcccc6c/images/7767a2fcef058ad261b1f86d77bbaf17134c6a11f85ffe23857bf0b53f46abdf.png"
                alt="Guide Orb"
                className="guru-orb"
                style={{ margin: '0 auto 1.5rem' }}
              />
              <p style={{ fontFamily: 'Cormorant Garamond', fontSize: '1.25rem', fontStyle: 'italic' }}>
                "The best questions don't have easy answers."
              </p>
              <p className="mt-4" style={{ fontSize: '0.9375rem' }}>
                Curious about philosophy, traditions, or just want to explore? I'm here for it.
              </p>
            </div>
          )}
          {messages.map((msg, idx) => (
            <div key={idx} className={`chat-bubble ${msg.role}`} data-testid={`chat-message-${idx}`}>
              {msg.content}
            </div>
          ))}
          {loading && (
            <div className="chat-bubble assistant" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
              <span>Thinking...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <div className="chat-input-container">
          <input
            type="text"
            className="chat-input"
            placeholder={t.chatPlaceholder}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && sendMessage()}
            disabled={loading}
            data-testid="chat-input"
          />
          <button 
            className="btn-primary" 
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            style={{ padding: '0.875rem 1.25rem' }}
            data-testid="chat-send-btn"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};


// Culture Content Data
const CULTURE_CONTENT = {
  china: {
    en: {
      name: "From China",
      tagline: "Balance, harmony, and the art of living with flow",
      intro: "Chinese philosophy offers timeless perspectives on balance, harmony, and finding your place in the natural order. From Confucian ethics to Taoist flow, these ideas invite reflection on how we relate to ourselves, others, and the world around us.",
      sections: [
        {
          title: "Finding Balance",
          content: "The concept of yin and yang isn't just an abstract symbol—it's about recognizing that life contains complementary forces: action and rest, speaking and listening, giving and receiving. Rather than fighting for one extreme, Chinese thought invites us to dance between them."
        },
        {
          title: "Going With the Flow",
          content: "Taoism suggests that sometimes the most powerful action is non-action—letting things unfold naturally rather than forcing outcomes. Think of water: it's soft, yet it shapes stone over time. What if we applied that patience to our own lives?"
        },
        {
          title: "Community & Responsibility",
          content: "Confucian ideas emphasize our roles in relationships—as children, parents, friends, citizens. It's less about rules and more about asking: What do I owe to those around me? How do my actions ripple outward?"
        },
        {
          title: "Living Simply",
          content: "From Zen gardens to minimalist aesthetics, Chinese and East Asian cultures have long explored the beauty of simplicity. Less isn't about deprivation—it's about clarity, space, and noticing what truly matters."
        }
      ]
    },
    es: {
      name: "Desde China",
      tagline: "Equilibrio, armonía y el arte de vivir con fluidez",
      intro: "La filosofía china ofrece perspectivas atemporales sobre el equilibrio, la armonía y encontrar tu lugar en el orden natural.",
      sections: [
        {
          title: "Encontrar el Equilibrio",
          content: "El concepto de yin y yang no es solo un símbolo abstracto: se trata de reconocer que la vida contiene fuerzas complementarias."
        },
        {
          title: "Fluir con la Corriente",
          content: "El taoísmo sugiere que a veces la acción más poderosa es la no-acción: dejar que las cosas se desarrollen naturalmente."
        },
        {
          title: "Comunidad y Responsabilidad",
          content: "Las ideas confucianas enfatizan nuestros roles en las relaciones. Se trata de preguntar: ¿Qué debo a los que me rodean?"
        },
        {
          title: "Vivir con Simplicidad",
          content: "Las culturas chinas y del este asiático han explorado durante mucho tiempo la belleza de la simplicidad."
        }
      ]
    },
    it: {
      name: "Dalla Cina",
      tagline: "Equilibrio, armonia e l'arte di vivere con fluidità",
      intro: "La filosofia cinese offre prospettive senza tempo su equilibrio, armonia e trovare il proprio posto nell'ordine naturale.",
      sections: [
        {
          title: "Trovare l'Equilibrio",
          content: "Il concetto di yin e yang non è solo un simbolo astratto: riguarda il riconoscere che la vita contiene forze complementari."
        },
        {
          title: "Fluire con la Corrente",
          content: "Il taoismo suggerisce che a volte l'azione più potente è la non-azione: lasciare che le cose si svolgano naturalmente."
        },
        {
          title: "Comunità e Responsabilità",
          content: "Le idee confuciane enfatizzano i nostri ruoli nelle relazioni. Si tratta di chiedersi: Cosa devo a chi mi circonda?"
        },
        {
          title: "Vivere con Semplicità",
          content: "Le culture cinesi e dell'Asia orientale hanno a lungo esplorato la bellezza della semplicità."
        }
      ]
    },
    fr: {
      name: "De Chine",
      tagline: "Équilibre, harmonie et l'art de vivre avec fluidité",
      intro: "La philosophie chinoise offre des perspectives intemporelles sur l'équilibre, l'harmonie et trouver sa place dans l'ordre naturel.",
      sections: [
        {
          title: "Trouver l'Équilibre",
          content: "Le concept de yin et yang n'est pas qu'un symbole abstrait : il s'agit de reconnaître que la vie contient des forces complémentaires."
        },
        {
          title: "Suivre le Flux",
          content: "Le taoïsme suggère que parfois l'action la plus puissante est la non-action : laisser les choses se dérouler naturellement."
        },
        {
          title: "Communauté et Responsabilité",
          content: "Les idées confucéennes soulignent nos rôles dans les relations. Il s'agit de se demander : Que dois-je à ceux qui m'entourent?"
        },
        {
          title: "Vivre Simplement",
          content: "Les cultures chinoises et d'Asie de l'Est ont longtemps exploré la beauté de la simplicité."
        }
      ]
    },
    de: {
      name: "Aus China",
      tagline: "Balance, Harmonie und die Kunst, im Fluss zu leben",
      intro: "Die chinesische Philosophie bietet zeitlose Perspektiven auf Balance, Harmonie und das Finden des eigenen Platzes in der natürlichen Ordnung.",
      sections: [
        {
          title: "Balance Finden",
          content: "Das Konzept von Yin und Yang ist nicht nur ein abstraktes Symbol: Es geht darum zu erkennen, dass das Leben komplementäre Kräfte enthält."
        },
        {
          title: "Mit dem Fluss Gehen",
          content: "Der Taoismus legt nahe, dass manchmal die mächtigste Handlung das Nicht-Handeln ist: Dinge natürlich geschehen zu lassen."
        },
        {
          title: "Gemeinschaft und Verantwortung",
          content: "Konfuzianische Ideen betonen unsere Rollen in Beziehungen. Es geht darum zu fragen: Was schulde ich denen um mich herum?"
        },
        {
          title: "Einfach Leben",
          content: "Chinesische und ostasiatische Kulturen haben lange die Schönheit der Einfachheit erforscht."
        }
      ]
    },
    ru: {
      name: "Из Китая",
      tagline: "Баланс, гармония и искусство жить в потоке",
      intro: "Китайская философия предлагает вечные перспективы баланса, гармонии и поиска своего места в естественном порядке.",
      sections: [
        {
          title: "Поиск Баланса",
          content: "Концепция инь и ян — это не просто абстрактный символ: речь идет о признании того, что жизнь содержит дополняющие силы."
        },
        {
          title: "Плыть по Течению",
          content: "Даосизм предполагает, что иногда самое мощное действие — это недействие: позволить вещам разворачиваться естественно."
        },
        {
          title: "Сообщество и Ответственность",
          content: "Конфуцианские идеи подчеркивают наши роли в отношениях. Речь о вопросе: Что я должен окружающим?"
        },
        {
          title: "Жить Просто",
          content: "Китайские и восточноазиатские культуры давно исследуют красоту простоты."
        }
      ]
    }
  },
  japan: {
    en: {
      name: "From Japan",
      tagline: "Mindfulness, impermanence, and the beauty of the everyday",
      intro: "Japanese thought invites us to slow down and notice: the changing seasons, the imperfection in handmade things, the fleeting beauty of a moment. It's a philosophy of presence, acceptance, and finding profound meaning in simple acts.",
      sections: [
        {
          title: "Embracing Impermanence",
          content: "Mono no aware—the 'pathos of things'—is about feeling the bittersweet beauty of life's transience. Cherry blossoms fall, seasons change, nothing lasts. Rather than fighting this, Japanese philosophy asks: What if we appreciated things more deeply because they're temporary?"
        },
        {
          title: "Wabi-Sabi: Finding Beauty in Imperfection",
          content: "Wabi-sabi celebrates the worn, the weathered, the incomplete. A cracked teacup tells a story. An aging wooden temple shows character. This perspective offers an antidote to our culture of perfectionism and endless upgrades."
        },
        {
          title: "Ikigai: Your Reason for Being",
          content: "Ikigai asks a simple question: What gets you out of bed in the morning? It's where your passion, mission, vocation, and profession meet. It's not about grand ambitions—sometimes it's as simple as tending a garden or making tea for a friend."
        },
        {
          title: "Mindful Rituals",
          content: "From tea ceremonies to forest bathing (shinrin-yoku), Japanese culture elevates everyday acts into moments of awareness. The idea isn't to add more to your schedule—it's to be fully present for what you're already doing."
        }
      ]
    },
    es: {
      name: "Desde Japón",
      tagline: "Atención plena, impermanencia y la belleza de lo cotidiano",
      intro: "El pensamiento japonés nos invita a desacelerar y notar: las estaciones cambiantes, la imperfección en las cosas hechas a mano.",
      sections: [
        {
          title: "Abrazar la Impermanencia",
          content: "Mono no aware es sentir la belleza agridulce de la transitoriedad de la vida. Las flores de cerezo caen, las estaciones cambian."
        },
        {
          title: "Wabi-Sabi: Belleza en la Imperfección",
          content: "Wabi-sabi celebra lo desgastado, lo deteriorado, lo incompleto. Una taza de té agrietada cuenta una historia."
        },
        {
          title: "Ikigai: Tu Razón de Ser",
          content: "Ikigai pregunta: ¿Qué te hace levantarte por la mañana? Es donde tu pasión, misión, vocación y profesión se encuentran."
        },
        {
          title: "Rituales Conscientes",
          content: "Desde las ceremonias del té hasta los baños de bosque, la cultura japonesa eleva los actos cotidianos."
        }
      ]
    },
    it: {
      name: "Dal Giappone",
      tagline: "Consapevolezza, impermanenza e la bellezza del quotidiano",
      intro: "Il pensiero giapponese ci invita a rallentare e notare: le stagioni che cambiano, l'imperfezione nelle cose fatte a mano.",
      sections: [
        {
          title: "Abbracciare l'Impermanenza",
          content: "Mono no aware è sentire la bellezza agrodolce della transitorietà della vita. I fiori di ciliegio cadono, le stagioni cambiano."
        },
        {
          title: "Wabi-Sabi: Bellezza nell'Imperfezione",
          content: "Wabi-sabi celebra il logoro, il deteriorato, l'incompleto. Una tazza da tè crepata racconta una storia."
        },
        {
          title: "Ikigai: La Tua Ragione d'Essere",
          content: "Ikigai chiede: Cosa ti fa alzare al mattino? È dove passione, missione, vocazione e professione si incontrano."
        },
        {
          title: "Rituali Consapevoli",
          content: "Dalle cerimonie del tè ai bagni nella foresta, la cultura giapponese eleva gli atti quotidiani."
        }
      ]
    },
    fr: {
      name: "Du Japon",
      tagline: "Pleine conscience, impermanence et la beauté du quotidien",
      intro: "La pensée japonaise nous invite à ralentir et à remarquer : les saisons changeantes, l'imperfection dans les choses faites à la main.",
      sections: [
        {
          title: "Embrasser l'Impermanence",
          content: "Mono no aware, c'est ressentir la beauté douce-amère de la fugacité de la vie. Les fleurs de cerisier tombent, les saisons changent."
        },
        {
          title: "Wabi-Sabi : Beauté dans l'Imperfection",
          content: "Wabi-sabi célèbre l'usé, le vieilli, l'incomplet. Une tasse à thé fissurée raconte une histoire."
        },
        {
          title: "Ikigai : Votre Raison d'Être",
          content: "Ikigai demande : Qu'est-ce qui vous fait lever le matin ? C'est là où passion, mission, vocation et profession se rencontrent."
        },
        {
          title: "Rituels Conscients",
          content: "Des cérémonies du thé aux bains de forêt, la culture japonaise élève les actes quotidiens."
        }
      ]
    },
    de: {
      name: "Aus Japan",
      tagline: "Achtsamkeit, Vergänglichkeit und die Schönheit des Alltags",
      intro: "Japanisches Denken lädt uns ein, langsamer zu werden und zu bemerken: die wechselnden Jahreszeiten, die Unvollkommenheit handgemachter Dinge.",
      sections: [
        {
          title: "Vergänglichkeit Umarmen",
          content: "Mono no aware bedeutet, die bittersüße Schönheit der Vergänglichkeit des Lebens zu fühlen. Kirschblüten fallen, Jahreszeiten wechseln."
        },
        {
          title: "Wabi-Sabi: Schönheit in der Unvollkommenheit",
          content: "Wabi-sabi feiert das Abgenutzte, das Verwitterte, das Unvollständige. Eine gesprungene Teeschale erzählt eine Geschichte."
        },
        {
          title: "Ikigai: Ihr Lebensgrund",
          content: "Ikigai fragt: Was lässt Sie morgens aufstehen? Es ist dort, wo Leidenschaft, Mission, Berufung und Beruf zusammentreffen."
        },
        {
          title: "Achtsame Rituale",
          content: "Von Teezeremonien bis zu Waldbädern hebt die japanische Kultur alltägliche Handlungen hervor."
        }
      ]
    },
    ru: {
      name: "Из Японии",
      tagline: "Осознанность, непостоянство и красота повседневного",
      intro: "Японская мысль приглашает нас замедлиться и заметить: меняющиеся сезоны, несовершенство в рукотворных вещах.",
      sections: [
        {
          title: "Принятие Непостоянства",
          content: "Моно но аварэ — это чувство горько-сладкой красоты быстротечности жизни. Вишневые цветы падают, сезоны меняются."
        },
        {
          title: "Ваби-Саби: Красота в Несовершенстве",
          content: "Ваби-саби празднует изношенное, выветренное, неполное. Треснутая чайная чашка рассказывает историю."
        },
        {
          title: "Икигай: Ваша Причина Существования",
          content: "Икигай спрашивает: Что заставляет вас вставать утром? Это место встречи страсти, миссии, призвания и профессии."
        },
        {
          title: "Осознанные Ритуалы",
          content: "От чайных церемоний до лесных ванн японская культура возвышает повседневные действия."
        }
      ]
    }
  },
  myanmar: {
    en: {
      name: "From Myanmar",
      tagline: "Compassion, mindfulness, and living with loving-kindness",
      intro: "Myanmar's philosophical traditions are deeply influenced by Theravada Buddhism, emphasizing mindfulness (sati), compassion (metta), and the practice of non-harm. These aren't abstract ideals—they're practices for cultivating inner peace and extending kindness to all beings.",
      sections: [
        {
          title: "Metta: Loving-Kindness",
          content: "Metta meditation starts with yourself—wishing yourself peace, health, happiness. Then it expands: to loved ones, to strangers, even to difficult people. The practice asks: What if we approached everyone, including ourselves, with genuine goodwill?"
        },
        {
          title: "Mindfulness in Daily Life",
          content: "Burmese mindfulness tradition isn't about sitting on a cushion for hours. It's about bringing awareness to everyday actions: walking, eating, washing dishes. Each moment becomes an opportunity to be present rather than lost in thought."
        },
        {
          title: "The Middle Path",
          content: "Neither indulgence nor denial—the middle path suggests balance. It's not about perfection or strict rules, but about noticing extremes in your life and gently finding a sustainable center. What does 'enough' look like for you?"
        },
        {
          title: "Interconnectedness",
          content: "Myanmar philosophy emphasizes that we're not isolated individuals—our actions affect others, and their wellbeing is tied to ours. This isn't just poetic; it's practical: How do your choices ripple outward into your family, community, and world?"
        }
      ]
    },
    es: {
      name: "Desde Myanmar",
      tagline: "Compasión, atención plena y vivir con bondad amorosa",
      intro: "Las tradiciones filosóficas de Myanmar están profundamente influenciadas por el budismo Theravada.",
      sections: [
        {
          title: "Metta: Bondad Amorosa",
          content: "La meditación metta comienza contigo mismo: deseándote paz, salud, felicidad. Luego se expande a todos los seres."
        },
        {
          title: "Atención Plena en la Vida Diaria",
          content: "La tradición birmana de atención plena trata de llevar conciencia a las acciones cotidianas."
        },
        {
          title: "El Camino Medio",
          content: "Ni indulgencia ni negación: el camino medio sugiere equilibrio. Se trata de encontrar un centro sostenible."
        },
        {
          title: "Interconexión",
          content: "La filosofía de Myanmar enfatiza que nuestras acciones afectan a otros, y su bienestar está ligado al nuestro."
        }
      ]
    },
    it: {
      name: "Dal Myanmar",
      tagline: "Compassione, consapevolezza e vivere con gentilezza amorevole",
      intro: "Le tradizioni filosofiche del Myanmar sono profondamente influenzate dal buddismo Theravada.",
      sections: [
        {
          title: "Metta: Gentilezza Amorevole",
          content: "La meditazione metta inizia con te stesso: augurando a te stesso pace, salute, felicità. Poi si espande a tutti gli esseri."
        },
        {
          title: "Consapevolezza nella Vita Quotidiana",
          content: "La tradizione birmana della consapevolezza riguarda il portare consapevolezza alle azioni quotidiane."
        },
        {
          title: "La Via di Mezzo",
          content: "Né indulgenza né negazione: la via di mezzo suggerisce equilibrio. Si tratta di trovare un centro sostenibile."
        },
        {
          title: "Interconnessione",
          content: "La filosofia del Myanmar sottolinea che le nostre azioni influenzano gli altri, e il loro benessere è legato al nostro."
        }
      ]
    },
    fr: {
      name: "Du Myanmar",
      tagline: "Compassion, pleine conscience et vivre avec bienveillance",
      intro: "Les traditions philosophiques du Myanmar sont profondément influencées par le bouddhisme Theravada.",
      sections: [
        {
          title: "Metta : Bienveillance",
          content: "La méditation metta commence avec vous-même : vous souhaitant paix, santé, bonheur. Puis elle s'étend à tous les êtres."
        },
        {
          title: "Pleine Conscience au Quotidien",
          content: "La tradition birmane de la pleine conscience consiste à apporter la conscience aux actions quotidiennes."
        },
        {
          title: "La Voie du Milieu",
          content: "Ni indulgence ni déni : la voie du milieu suggère l'équilibre. Il s'agit de trouver un centre durable."
        },
        {
          title: "Interconnexion",
          content: "La philosophie du Myanmar souligne que nos actions affectent les autres, et leur bien-être est lié au nôtre."
        }
      ]
    },
    de: {
      name: "Aus Myanmar",
      tagline: "Mitgefühl, Achtsamkeit und Leben mit liebevoller Güte",
      intro: "Die philosophischen Traditionen Myanmars sind stark vom Theravada-Buddhismus beeinflusst.",
      sections: [
        {
          title: "Metta: Liebevolle Güte",
          content: "Metta-Meditation beginnt bei sich selbst: sich Frieden, Gesundheit, Glück wünschen. Dann dehnt sie sich auf alle Wesen aus."
        },
        {
          title: "Achtsamkeit im Alltag",
          content: "Die birmanische Achtsamkeitstradition geht darum, Bewusstsein in alltägliche Handlungen zu bringen."
        },
        {
          title: "Der Mittlere Weg",
          content: "Weder Genuss noch Verleugnung: Der mittlere Weg schlägt Gleichgewicht vor. Es geht darum, eine nachhaltige Mitte zu finden."
        },
        {
          title: "Vernetzung",
          content: "Die Philosophie Myanmars betont, dass unsere Handlungen andere beeinflussen und ihr Wohlergehen mit unserem verbunden ist."
        }
      ]
    },
    ru: {
      name: "Из Мьянмы",
      tagline: "Сострадание, осознанность и жизнь с любящей добротой",
      intro: "Философские традиции Мьянмы глубоко находятся под влиянием буддизма Тхеравады.",
      sections: [
        {
          title: "Метта: Любящая Доброта",
          content: "Медитация метты начинается с себя: желая себе мира, здоровья, счастья. Затем расширяется на всех существ."
        },
        {
          title: "Осознанность в Повседневной Жизни",
          content: "Бирманская традиция осознанности заключается в привнесении осознанности в повседневные действия."
        },
        {
          title: "Срединный Путь",
          content: "Ни потакания, ни отрицания: срединный путь предлагает баланс. Речь о нахождении устойчивого центра."
        },
        {
          title: "Взаимосвязанность",
          content: "Философия Мьянмы подчеркивает, что наши действия влияют на других, и их благополучие связано с нашим."
        }
      ]
    }
  },
  srilanka: {
    en: {
      name: "From Sri Lanka",
      tagline: "Wisdom, harmony, and the path to inner peace",
      intro: "Sri Lankan thought blends Buddhist mindfulness with the island's rich multicultural heritage. It emphasizes compassion, ethical living, and the pursuit of wisdom—not as abstract knowledge, but as practical insight into how to live well.",
      sections: [
        {
          title: "The Noble Eightfold Path",
          content: "This ancient framework isn't a rigid set of commandments—it's a practical guide for ethical and mindful living. Right speech, right action, right livelihood: What would your life look like if you brought more intentionality to these areas?"
        },
        {
          title: "Anatta: Letting Go of the Ego",
          content: "The concept of 'non-self' suggests that much of our suffering comes from clinging to a fixed idea of who we are. It's not about losing yourself—it's about freedom from rigid self-definitions. Who would you be if you weren't constantly defending an image?"
        },
        {
          title: "Vipassana: Seeing Clearly",
          content: "Vipassana meditation, rooted in Sri Lankan tradition, is about seeing things as they truly are—not through the filter of our hopes, fears, or biases. It's radical honesty: observing your thoughts and sensations without judgment or attachment."
        },
        {
          title: "Living Ethically",
          content: "Sri Lankan philosophy emphasizes sila (ethical conduct): not harming, not taking what isn't given, speaking truthfully. These aren't just rules—they're invitations to consider the impact of our choices on ourselves and others."
        }
      ]
    },
    es: {
      name: "Desde Sri Lanka",
      tagline: "Sabiduría, armonía y el camino hacia la paz interior",
      intro: "El pensamiento de Sri Lanka combina la atención plena budista con el rico patrimonio multicultural de la isla.",
      sections: [
        {
          title: "El Noble Óctuple Sendero",
          content: "Este marco antiguo es una guía práctica para una vida ética y consciente. Habla correcta, acción correcta, sustento correcto."
        },
        {
          title: "Anatta: Soltar el Ego",
          content: "El concepto de 'no-yo' sugiere que gran parte de nuestro sufrimiento proviene de aferrarnos a una idea fija de quiénes somos."
        },
        {
          title: "Vipassana: Ver con Claridad",
          content: "La meditación vipassana trata de ver las cosas como realmente son, sin el filtro de nuestras esperanzas o miedos."
        },
        {
          title: "Vivir Éticamente",
          content: "La filosofía de Sri Lanka enfatiza sila (conducta ética): no dañar, no tomar lo que no se da, hablar con verdad."
        }
      ]
    },
    it: {
      name: "Dallo Sri Lanka",
      tagline: "Saggezza, armonia e il percorso verso la pace interiore",
      intro: "Il pensiero dello Sri Lanka fonde la consapevolezza buddista con il ricco patrimonio multiculturale dell'isola.",
      sections: [
        {
          title: "Il Nobile Ottuplice Sentiero",
          content: "Questo antico quadro è una guida pratica per una vita etica e consapevole. Retta parola, retta azione, retto sostentamento."
        },
        {
          title: "Anatta: Lasciar Andare l'Ego",
          content: "Il concetto di 'non-sé' suggerisce che gran parte della nostra sofferenza deriva dall'attaccamento a un'idea fissa di chi siamo."
        },
        {
          title: "Vipassana: Vedere Chiaramente",
          content: "La meditazione vipassana riguarda il vedere le cose come sono realmente, senza il filtro delle nostre speranze o paure."
        },
        {
          title: "Vivere Eticamente",
          content: "La filosofia dello Sri Lanka enfatizza sila (condotta etica): non nuocere, non prendere ciò che non è dato, parlare con verità."
        }
      ]
    },
    fr: {
      name: "Du Sri Lanka",
      tagline: "Sagesse, harmonie et le chemin vers la paix intérieure",
      intro: "La pensée sri-lankaise mêle la pleine conscience bouddhiste au riche patrimoine multiculturel de l'île.",
      sections: [
        {
          title: "Le Noble Chemin Octuple",
          content: "Ce cadre ancien est un guide pratique pour une vie éthique et consciente. Parole juste, action juste, moyens d'existence justes."
        },
        {
          title: "Anatta : Lâcher l'Ego",
          content: "Le concept de 'non-soi' suggère qu'une grande partie de notre souffrance vient de l'attachement à une idée fixe de qui nous sommes."
        },
        {
          title: "Vipassana : Voir Clairement",
          content: "La méditation vipassana consiste à voir les choses telles qu'elles sont, sans le filtre de nos espoirs ou de nos peurs."
        },
        {
          title: "Vivre Éthiquement",
          content: "La philosophie du Sri Lanka met l'accent sur sila (conduite éthique) : ne pas nuire, ne pas prendre ce qui n'est pas donné, parler avec vérité."
        }
      ]
    },
    de: {
      name: "Aus Sri Lanka",
      tagline: "Weisheit, Harmonie und der Weg zum inneren Frieden",
      intro: "Das sri-lankische Denken verbindet buddhistische Achtsamkeit mit dem reichen multikulturellen Erbe der Insel.",
      sections: [
        {
          title: "Der Edle Achtfache Pfad",
          content: "Dieser alte Rahmen ist ein praktischer Leitfaden für ethisches und achtsames Leben. Rechte Rede, rechtes Handeln, rechter Lebensunterhalt."
        },
        {
          title: "Anatta: Das Ego Loslassen",
          content: "Das Konzept des 'Nicht-Selbst' legt nahe, dass ein Großteil unseres Leidens aus dem Festhalten an einer festen Vorstellung davon entsteht, wer wir sind."
        },
        {
          title: "Vipassana: Klar Sehen",
          content: "Vipassana-Meditation geht darum, die Dinge so zu sehen, wie sie wirklich sind, ohne den Filter unserer Hoffnungen oder Ängste."
        },
        {
          title: "Ethisch Leben",
          content: "Die sri-lankische Philosophie betont sila (ethisches Verhalten): nicht schaden, nicht nehmen, was nicht gegeben ist, wahrheitsgemäß sprechen."
        }
      ]
    },
    ru: {
      name: "Из Шри-Ланки",
      tagline: "Мудрость, гармония и путь к внутреннему покою",
      intro: "Мысль Шри-Ланки сочетает буддийскую осознанность с богатым мультикультурным наследием острова.",
      sections: [
        {
          title: "Благородный Восьмеричный Путь",
          content: "Эта древняя структура — практическое руководство для этичной и осознанной жизни. Правильная речь, правильное действие, правильные средства к существованию."
        },
        {
          title: "Анатта: Отпускание Эго",
          content: "Концепция 'не-я' предполагает, что большая часть нашего страдания происходит от цепляния за фиксированное представление о том, кто мы."
        },
        {
          title: "Випассана: Ясное Видение",
          content: "Медитация випассаны заключается в том, чтобы видеть вещи такими, какие они есть, без фильтра наших надежд или страхов."
        },
        {
          title: "Этичная Жизнь",
          content: "Философия Шри-Ланки подчеркивает сила (этическое поведение): не вредить, не брать то, что не дано, говорить правду."
        }
      ]
    }
  }
};

// Culture Modal Component
const CultureModal = ({ isOpen, onClose, culture, language }) => {
  const modalRef = useRef(null);
  
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  
  if (!isOpen || !culture) return null;
  
  const content = CULTURE_CONTENT[culture]?.[language] || CULTURE_CONTENT[culture]?.en;
  
  return (
    <div 
      className="modal-overlay" 
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="culture-modal"
    >
      <div className="modal-content culture-modal" ref={modalRef} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="modal-close" onClick={onClose} data-testid="culture-modal-close">
          <X size={24} />
        </button>
        
        <div style={{ padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 300, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              {content.name}
            </h2>
            <p style={{ fontSize: '1rem', color: 'var(--text-accent)', fontStyle: 'italic' }}>
              {content.tagline}
            </p>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: '1.125rem', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
              {content.intro}
            </p>
          </div>
          
          <div className="culture-sections">
            {content.sections.map((section, idx) => (
              <div key={idx} style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 500, marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                  {section.title}
                </h3>
                <p style={{ fontSize: '1rem', lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                  {section.content}
                </p>
              </div>
            ))}
          </div>
          
          <div style={{ 
            marginTop: '2rem', 
            padding: '1.5rem', 
            background: 'var(--brand-primary)', 
            borderRadius: '0.75rem',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-primary)', opacity: 0.8, lineHeight: 1.6 }}>
              {language === 'en' ? (
                <>These perspectives are offered as invitations to reflect—not as prescriptions or religious doctrine. 
                We encourage exploring multiple sources and forming your own understanding.</>
              ) : (
                'These perspectives are offered as invitations to reflect—not as prescriptions or religious doctrine.'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// From Our Desk Modal Component
const FromOurDeskModal = ({ isOpen, onClose, language }) => {
  const modalRef = useRef(null);
  
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="modal-overlay" 
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="from-our-desk-modal"
    >
      <div className="modal-content" ref={modalRef} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="modal-close" onClick={onClose} data-testid="from-our-desk-modal-close">
          <X size={24} />
        </button>
        
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <BookOpen size={48} style={{ color: 'var(--text-accent)', marginBottom: '1rem' }} />
          <h2 style={{ fontSize: '2rem', fontWeight: 300, marginBottom: '1rem', color: 'var(--text-primary)' }}>
            From Our Desk
          </h2>
          <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: 1.7 }}>
            {language === 'en' 
              ? 'This space is being prepared for thoughtful reflections, curated insights, and updates from our team.'
              : 'This space is being prepared for content.'}
          </p>
          
          <div style={{ 
            padding: '3rem 2rem', 
            background: 'var(--bg-secondary)', 
            borderRadius: '0.75rem',
            border: '2px dashed var(--border-color)'
          }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', opacity: 0.7 }}>
              {language === 'en' 
                ? 'Content coming soon. Check back for articles, reflections, and updates.'
                : 'Content coming soon.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = ({ t, onAskGuide, onFromOurDesk }) => (
  <section className="hero-section" data-testid="hero-section">
    <div className="hero-bg">
      <img 
        src="https://static.prod-images.emergentagent.com/jobs/b81b5205-22f3-4d75-87bf-f3580bcccc6c/images/2df677937567b853fdbbc6700a635c0d06c6ca267383d4640b8a5cfe6f4289dc.png"
        alt="Peace and community"
      />
      <div className="hero-overlay"></div>
    </div>
    <div className="hero-content animate-fade-in">
      <p className="accent-font" style={{ fontSize: '1.125rem', color: 'var(--text-accent)', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>
        Explore • Reflect • Connect
      </p>
      <p style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '1.5rem', letterSpacing: '0.02em' }}>
        {t.beliefLine}
      </p>
      <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 300, letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '1.5rem' }}>
        {t.tagline}
      </h1>
      <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto 1rem', lineHeight: 1.7 }}>
        {t.subtitle}
      </p>
      <p style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2rem', letterSpacing: '0.02em' }}>
        {t.beliefLine}
      </p>
      
      {/* From Our Desk */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button 
          className="btn-from-desk" 
          onClick={onFromOurDesk}
          data-testid="from-our-desk-btn"
        >
          <BookOpen size={18} />
          From Our Desk
        </button>
      </div>
      
      <div className="flex justify-center gap-4" style={{ flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={onAskGuide} data-testid="hero-ask-guide-btn">
          <MessageCircle size={20} />
          {t.askGuide}
        </button>
        <a href="#articles" className="btn-secondary" data-testid="hero-explore-btn">
          {t.startJourney}
        </a>
      </div>
    </div>
  </section>
);

// Articles Section
const ArticlesSection = ({ articles, language, t }) => (
  <section id="articles" className="section" style={{ background: 'var(--bg-secondary)' }} data-testid="articles-section">
    <div className="container">
      <div className="text-center mb-12">
        <h2 className="section-title">{t.articles}</h2>
        <p className="section-subtitle" style={{ margin: '0.5rem auto 0' }}>{t.articlesDesc}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6" style={{ gap: '1.5rem' }}>
        {articles.map((article, idx) => (
          <div key={article.id} className="soft-card article-card animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }} data-testid={`article-card-${idx}`}>
            {article.image_url && (
              <img src={article.image_url} alt={article.title[language] || article.title.en} className="article-image" />
            )}
            <div className="article-content">
              <span className="article-category">{article.category}</span>
              <h3 className="article-title">{article.title[language] || article.title.en}</h3>
              <p className="article-excerpt">{article.excerpt[language] || article.excerpt.en}</p>
              <div style={{ marginTop: 'auto', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button className="btn-secondary" style={{ padding: '0.625rem 1.25rem', fontSize: '0.875rem', alignSelf: 'flex-start' }}>
                  {t.readMore}
                </button>
                <ShareButtons title={article.title[language] || article.title.en} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Festivals Section
const FestivalsSection = ({ festivals, language, t }) => (
  <section id="festivals" className="section" data-testid="festivals-section">
    <div className="container">
      <div className="text-center mb-8">
        <h2 className="section-title">{t.festivals}</h2>
        <p className="section-subtitle" style={{ margin: '0.5rem auto 0' }}>{t.festivalsDesc}</p>
      </div>
      
      {/* Horizontal scroll container for mobile */}
      <div className="festival-scroll-container">
        <div className="festival-scroll">
          {festivals.map((festival, idx) => (
            <div key={festival.id} className="festival-card soft-card" data-testid={`festival-card-${idx}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <div className="festival-date-badge">
                  <span className="festival-day">{festival.day}</span>
                  <span className="festival-month">
                    {new Date(festival.date).toLocaleString('default', { month: 'short' })}
                  </span>
                </div>
                <h3 className="festival-name">{festival.name[language] || festival.name.en}</h3>
              </div>
              <p className="festival-description">
                {festival.description[language] || festival.description.en}
              </p>
              <div className="festival-practices">
                <p className="festival-practices-label">Practices</p>
                <p className="festival-practices-text">
                  {festival.practices[language] || festival.practices.en}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Meditation Section
const MeditationSection = ({ meditations, language, t }) => (
  <section id="meditation" className="section" style={{ background: 'var(--bg-secondary)' }} data-testid="meditation-section">
    <div className="container">
      <div className="bento-grid" style={{ alignItems: 'center' }}>
        <div className="md:col-span-5" style={{ gridColumn: 'span 5' }}>
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b81b5205-22f3-4d75-87bf-f3580bcccc6c/images/da4bf5f8cbbf74bc874ec48039da7aaf468f825b9f87b99f441bf703f1f2cf65.png"
            alt="Meditation"
            style={{ width: '100%', borderRadius: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}
          />
        </div>
        <div className="md:col-span-7" style={{ gridColumn: 'span 7', paddingLeft: '2rem' }}>
          <h2 className="section-title mb-4">{t.meditation}</h2>
          <p className="section-subtitle mb-8">{t.meditationDesc}</p>
          <div className="flex" style={{ flexDirection: 'column', gap: '1rem' }}>
            {meditations.map((guide, idx) => (
              <div key={guide.id} className="soft-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }} data-testid={`meditation-card-${idx}`}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '50%', 
                  background: 'var(--lavender-subtle)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Leaf size={24} style={{ color: 'var(--text-accent)' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '1.0625rem', fontWeight: 500, marginBottom: '0.25rem' }}>
                    {guide.title[language] || guide.title.en}
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {guide.duration_minutes} min · {guide.type}
                  </p>
                </div>
                <button className="btn-peach">Start</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Environment Section
const EnvironmentSection = ({ t }) => (
  <section className="section" data-testid="environment-section">
    <div className="container">
      <div className="bento-grid" style={{ alignItems: 'center' }}>
        <div className="md:col-span-7" style={{ gridColumn: 'span 7', paddingRight: '2rem' }}>
          <p className="overline mb-2" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-accent)' }}>
            Ancient Wisdom for Modern Challenges
          </p>
          <h2 className="section-title mb-4">{t.environment}</h2>
          <p className="section-subtitle mb-6">{t.environmentDesc}</p>
          <div style={{ background: 'var(--sand)', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <p className="accent-font" style={{ fontSize: '1.25rem', fontStyle: 'italic', color: 'var(--text-accent)', lineHeight: 1.6 }}>
              "The Earth is our mother. We are all her children. Whatever we do to the Earth, we do to ourselves."
            </p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>— Ancient Vedic teaching</p>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[
              'Non-violence toward all living beings',
              'Sacred hymns honoring Mother Earth',
              'The philosophy of universal family',
              'Traditional knowledge of plant life and agriculture'
            ].map((item, idx) => (
              <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
                <Leaf size={16} style={{ color: 'var(--brand-primary)' }} />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-5" style={{ gridColumn: 'span 5' }}>
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b81b5205-22f3-4d75-87bf-f3580bcccc6c/images/e03776cfb3d301d622e07e005dd9cb4f4f5170202016d416845cd4bb66cff291.png"
            alt="Environmental Wisdom"
            style={{ width: '100%', borderRadius: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}
          />
        </div>
      </div>
    </div>
  </section>
);

// Specialists Section
const SpecialistsSection = ({ specialists, language, t }) => (
  <section className="section" style={{ background: 'var(--bg-secondary)' }} data-testid="specialists-section">
    <div className="container">
      <div className="text-center mb-12">
        <h2 className="section-title">{t.specialists}</h2>
        <p className="section-subtitle" style={{ margin: '0.5rem auto 0' }}>{t.specialistsDesc}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {specialists.map((specialist, idx) => (
          <div key={specialist.id} className="soft-card specialist-card animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }} data-testid={`specialist-card-${idx}`}>
            {specialist.image_url ? (
              <img src={specialist.image_url} alt={specialist.name} className="specialist-avatar" />
            ) : (
              <div className="specialist-avatar" style={{ background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={40} style={{ color: 'var(--text-primary)' }} />
              </div>
            )}
            <h3 className="specialist-name">{specialist.name}</h3>
            <p className="specialist-role">{specialist.specialization[language] || specialist.specialization.en}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              {specialist.bio[language] || specialist.bio.en}
            </p>
            <div className="flex justify-center gap-2">
              {specialist.booking_link && (
                <a href={specialist.booking_link} target="_blank" rel="noopener noreferrer" className="btn-peach" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
                  {t.bookSession} <ExternalLink size={14} />
                </a>
              )}
              {specialist.contact_link && !specialist.booking_link && (
                <a href={specialist.contact_link} className="btn-secondary" style={{ padding: '0.625rem 1rem', fontSize: '0.875rem', textDecoration: 'none' }}>
                  {t.contact}
                </a>
              )}
            </div>
            {specialist.is_premium && (
              <span style={{ 
                display: 'inline-block', 
                marginTop: '1rem', 
                fontSize: '0.6875rem', 
                padding: '0.25rem 0.75rem', 
                background: 'var(--peach-accent)', 
                borderRadius: '9999px',
                fontWeight: 500
              }}>
                Premium Guide
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="text-center mt-8" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
        External links are provided for your convenience. Sanatan Today does not endorse or guarantee any services.
      </p>
    </div>
  </section>
);

// Community Section
const CommunitySection = ({ posts, language, t }) => {
  const [activeTab, setActiveTab] = useState('discussion');
  const filteredPosts = posts.filter(p => p.category === activeTab || activeTab === 'all');
  
  return (
    <section id="community" className="section" data-testid="community-section">
      <div className="container">
        <div className="text-center mb-8">
          <h2 className="section-title">{t.community}</h2>
          <p className="section-subtitle" style={{ margin: '0.5rem auto 0' }}>{t.communityDesc}</p>
        </div>
        
        <div className="flex justify-center mb-8">
          <div className="tabs" data-testid="community-tabs">
            <button 
              className={`tab ${activeTab === 'discussion' ? 'active' : ''}`}
              onClick={() => setActiveTab('discussion')}
              data-testid="tab-discussions"
            >
              {t.discussions}
            </button>
            <button 
              className={`tab ${activeTab === 'question' ? 'active' : ''}`}
              onClick={() => setActiveTab('question')}
              data-testid="tab-questions"
            >
              {t.questions}
            </button>
            <button 
              className={`tab ${activeTab === 'article' ? 'active' : ''}`}
              onClick={() => setActiveTab('article')}
              data-testid="tab-articles"
            >
              {t.articles}
            </button>
          </div>
        </div>
        
        <div className="soft-card" style={{ maxWidth: '800px', margin: '0 auto', overflow: 'hidden' }}>
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post, idx) => (
              <div key={post.id} className="forum-post" data-testid={`forum-post-${idx}`}>
                <h4 className="forum-title">{post.title}</h4>
                <div className="forum-meta">
                  <span>By {post.author_name}</span>
                  <span>{post.replies_count} replies</span>
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center" style={{ padding: '3rem' }}>
              <Users size={48} style={{ color: 'var(--brand-primary)', margin: '0 auto 1rem' }} />
              <p style={{ color: 'var(--text-secondary)' }}>Be the first to start a conversation!</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ t }) => (
  <footer className="footer" data-testid="footer">
    <div className="footer-content">
      <p className="footer-tagline">{t.footerTagline}</p>
      <div className="footer-links">
        <a href="#articles" className="footer-link">{t.navArticles}</a>
        <a href="#festivals" className="footer-link">{t.navFestivals}</a>
        <a href="#meditation" className="footer-link">{t.navMeditation}</a>
        <a href="#community" className="footer-link">{t.navCommunity}</a>
      </div>
      
      {/* Share site */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <ShareButtons title="Living Heritage - Timeless thoughts. New world." />
      </div>
      
      <p className="footer-copyright">
        © {new Date().getFullYear()} {APP_NAME}. Made with care for curious minds everywhere.
      </p>
      
      {/* Emergent Acknowledgment */}
      <div className="acknowledgment-section">
        <p className="acknowledgment-text">
          Proudly built with
        </p>
        <a 
          href="https://emergent.sh" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="acknowledgment-link"
          data-testid="emergent-acknowledgment"
        >
          Emergent AI Platform
        </a>
        <p className="acknowledgment-text" style={{ marginTop: '0.5rem' }}>
          Transforming ideas into reality — we highly recommend it for creators and changemakers.
        </p>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  const [language, setLanguage] = useState('en');
  const [chatOpen, setChatOpen] = useState(false);
  const [cultureModalOpen, setCultureModalOpen] = useState(false);
  const [selectedCulture, setSelectedCulture] = useState(null);
  const [fromOurDeskOpen, setFromOurDeskOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [meditations, setMeditations] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [forumPosts, setForumPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const t = TRANSLATIONS[language];
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes, festivalsRes, meditationsRes, specialistsRes, postsRes] = await Promise.all([
          axios.get(`${API}/articles`),
          axios.get(`${API}/festivals`),
          axios.get(`${API}/meditations`),
          axios.get(`${API}/specialists`),
          axios.get(`${API}/forum/posts?language=${language}`)
        ]);
        
        setArticles(articlesRes.data);
        setFestivals(festivalsRes.data);
        setMeditations(meditationsRes.data);
        setSpecialists(specialistsRes.data);
        setForumPosts(postsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [language]);
  
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div data-testid="app-container">
      <Navigation 
        language={language} 
        setLanguage={setLanguage} 
        t={t} 
        onAskGuide={() => setChatOpen(true)}
        onSelectCulture={(culture) => {
          setSelectedCulture(culture);
          setCultureModalOpen(true);
        }}
      />
      
      <main style={{ paddingTop: '72px' }}>
        <HeroSection 
          t={t} 
          onAskGuide={() => setChatOpen(true)}
          onFromOurDesk={() => setFromOurDeskOpen(true)}
        />
        <ArticlesSection articles={articles} language={language} t={t} />
        <FestivalsSection festivals={festivals} language={language} t={t} />
        <MeditationSection meditations={meditations} language={language} t={t} />
        <EnvironmentSection t={t} />
        <EmailSignup language={language} t={t} />
        <SpecialistsSection specialists={specialists} language={language} t={t} />
        <CommunitySection posts={forumPosts} language={language} t={t} />
      </main>
      
      <DisclaimerSection language={language} />
      
      <Footer t={t} />
      
      <ChatModal 
        isOpen={chatOpen} 
        onClose={() => setChatOpen(false)} 
        language={language}
        t={t}
      />
      
      <CultureModal
        isOpen={cultureModalOpen}
        onClose={() => setCultureModalOpen(false)}
        culture={selectedCulture}
        language={language}
      />
      
      <FromOurDeskModal
        isOpen={fromOurDeskOpen}
        onClose={() => setFromOurDeskOpen(false)}
        language={language}
      />
    </div>
  );
}

export default App;
